import { useEffect, useState, useRef } from 'react';
import { Layout } from '@/components/Layout';
import { Bell, Database, Palette, Upload, X, RotateCcw, Check, Image as ImageIcon, Loader2 } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import { useBranding } from '@/contexts/BrandingContext';
import { usePermissions } from '@/hooks/usePermissions';
import { api, companyEndpoints, getApiErrorMessage, settingsEndpoints } from '@/lib/api';
import { PERMISSIONS } from '@/lib/permissions';
import { toast } from 'sonner';

const COLOR_PRESETS = [
  { name: 'Escuro (Padrão)', sidebar: '#0f172a', accent: '#2563eb' },
  { name: 'Azul Marinho', sidebar: '#1e3a5f', accent: '#3b82f6' },
  { name: 'Azul Escuro', sidebar: '#172554', accent: '#60a5fa' },
  { name: 'Verde Floresta', sidebar: '#14532d', accent: '#22c55e' },
  { name: 'Verde Escuro', sidebar: '#1a2e1a', accent: '#4ade80' },
  { name: 'Roxo', sidebar: '#2e1065', accent: '#a855f7' },
  { name: 'Bordô', sidebar: '#450a0a', accent: '#ef4444' },
  { name: 'Cinzento', sidebar: '#374151', accent: '#6b7280' },
  { name: 'Preto', sidebar: '#09090b', accent: '#3b82f6' },
  { name: 'Branco', sidebar: '#ffffff', accent: '#2563eb' },
  { name: 'Creme', sidebar: '#fefce8', accent: '#ca8a04' },
  { name: 'Azul Claro', sidebar: '#eff6ff', accent: '#2563eb' },
];

const DEFAULT_SYSTEM_SETTINGS = {
  occurrenceEmail: true,
  pushNotifications: true,
  weeklyReports: false,
  dataRetention: '1ano',
};

const SETTING_ALIASES = {
  occurrenceEmail: ['occurrence_email_notifications', 'email_occurrences', 'occurrence_email', 'email_notifications'],
  pushNotifications: ['push_notifications', 'browser_push_notifications', 'push'],
  weeklyReports: ['weekly_reports', 'weekly_email_reports', 'weekly_report_email'],
  dataRetention: ['data_retention', 'retention_period', 'occurrence_history_retention'],
};

const COMPANY_FIELD_ALIASES = {
  id: ['id', 'company_id', 'companyId'],
  localLogo: ['localLogo', 'local_logo', 'branding_logo', 'company_logo', 'logo'],
  localName: ['localName', 'local_name', 'company_name', 'branding_name', 'companyName'],
  localSubtitle: ['localSubtitle', 'local_subtitle', 'company_subtitle', 'branding_subtitle', 'companySubtitle'],
};

const normalizeBoolean = (value, fallback) => {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value === 1;
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    if (['1', 'true', 'yes', 'on', 'sim'].includes(normalized)) return true;
    if (['0', 'false', 'no', 'off', 'nao'].includes(normalized)) return false;
  }
  return fallback;
};

const settingsArrayToObject = (settings) => settings.reduce((acc, item) => {
  const key = item?.key ?? item?.name ?? item?.setting ?? item?.code;
  if (!key) return acc;
  acc[key] = item?.value ?? item?.setting_value ?? item?.data ?? item?.enabled;
  return acc;
}, {});

const findSettingsSource = (payload) => {
  const candidates = [
    payload?.data?.settings,
    payload?.settings,
    payload?.data,
    payload,
  ];
  const source = candidates.find(candidate => candidate && typeof candidate === 'object');
  if (Array.isArray(source)) return settingsArrayToObject(source);
  return source || {};
};

const unwrapApiObject = (value) => {
  const source = value?.data && typeof value.data === 'object' && !Array.isArray(value.data)
    ? value.data
    : value;

  return source?.attributes && typeof source.attributes === 'object' && !Array.isArray(source.attributes)
    ? { ...source, ...source.attributes }
    : source;
};

const findCompanySource = (payload, companyId = null) => {
  const candidates = [
    payload?.data?.company,
    payload?.company,
    payload?.data?.current_company,
    payload?.current_company,
    payload?.data?.companies,
    payload?.companies,
    payload?.data,
    payload,
  ].filter(Boolean);

  for (const candidate of candidates) {
    if (Array.isArray(candidate)) {
      const companies = candidate.map(unwrapApiObject).filter(Boolean);
      const company = companyId
        ? companies.find(item => String(settingValue(item, COMPANY_FIELD_ALIASES.id)) === String(companyId))
        : companies[0];
      if (company) return company;
      continue;
    }

    const company = unwrapApiObject(candidate);
    if (company && typeof company === 'object') return company;
  }

  return {};
};

const settingValue = (source, keys) => {
  const key = keys.find(candidate => Object.prototype.hasOwnProperty.call(source, candidate));
  return key ? source[key] : undefined;
};

const hasRecognizedSettings = (source) => Object.values(SETTING_ALIASES)
  .some(keys => keys.some(key => Object.prototype.hasOwnProperty.call(source, key)));

const normalizeSystemSettings = (payload, fallback = DEFAULT_SYSTEM_SETTINGS) => {
  const source = findSettingsSource(payload);
  if (!hasRecognizedSettings(source)) return fallback;

  const occurrenceEmail = settingValue(source, SETTING_ALIASES.occurrenceEmail);
  const pushNotifications = settingValue(source, SETTING_ALIASES.pushNotifications);
  const weeklyReports = settingValue(source, SETTING_ALIASES.weeklyReports);
  const dataRetention = settingValue(source, SETTING_ALIASES.dataRetention);

  return {
    occurrenceEmail: normalizeBoolean(occurrenceEmail, fallback.occurrenceEmail),
    pushNotifications: normalizeBoolean(pushNotifications, fallback.pushNotifications),
    weeklyReports: normalizeBoolean(weeklyReports, fallback.weeklyReports),
    dataRetention: dataRetention || fallback.dataRetention,
  };
};

const normalizeCompanyInfo = (payload, fallback, companyId = null) => {
  const source = findCompanySource(payload, companyId);
  const localLogo = settingValue(source, COMPANY_FIELD_ALIASES.localLogo);
  const localName = settingValue(source, COMPANY_FIELD_ALIASES.localName);
  const localSubtitle = settingValue(source, COMPANY_FIELD_ALIASES.localSubtitle);

  return {
    id: settingValue(source, COMPANY_FIELD_ALIASES.id) ?? fallback.id ?? companyId ?? null,
    logo: localLogo ?? fallback.logo,
    companyName: localName ?? fallback.companyName,
    companySubtitle: localSubtitle ?? fallback.companySubtitle,
  };
};

const systemSettingsPayload = (settings) => ({
  occurrence_email_notifications: settings.occurrenceEmail,
  push_notifications: settings.pushNotifications,
  weekly_reports: settings.weeklyReports,
  data_retention: settings.dataRetention,
});

const companyInfoPayload = ({ id, logo, companyName, companySubtitle }) => ({
  ...(id ? { id, company_id: id } : {}),
  localLogo: logo,
  localName: companyName,
  localSubtitle: companySubtitle,
});

const shouldTryFallbackEndpoint = (error) => [404, 405].includes(error?.response?.status);

const fetchCompanyInfo = async () => {
  try {
    return await api.get(companyEndpoints.current);
  } catch (error) {
    if (!shouldTryFallbackEndpoint(error)) throw error;
    return api.get(companyEndpoints.list);
  }
};

const dataUrlToFile = async (dataUrl) => {
  const response = await fetch(dataUrl);
  const blob = await response.blob();
  const extension = blob.type.split('/')[1]?.replace('svg+xml', 'svg') || 'png';
  return new File([blob], `company-logo.${extension}`, { type: blob.type });
};

const updateCompanyInfo = async (payload) => {
  if (!payload.localLogo?.startsWith?.('data:')) {
    return api.post(companyEndpoints.update, payload);
  }

  const formData = new FormData();
  Object.entries(payload).forEach(([key, value]) => {
    if (key !== 'localLogo' && value !== null && value !== undefined) {
      formData.append(key, value);
    }
  });
  formData.append('localLogo', await dataUrlToFile(payload.localLogo));

  return api.post(companyEndpoints.update, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};

const SETTINGS_ACTION_PERMISSIONS = {
  create: PERMISSIONS.SETTINGS_CREATE,
  read: PERMISSIONS.SETTINGS_READ,
  update: PERMISSIONS.SETTINGS_UPDATE,
  delete: PERMISSIONS.SETTINGS_DELETE,
};

export const SettingsPage = () => {
  const { branding, updateBranding, resetBranding, DEFAULT_BRANDING } = useBranding();
  const { can } = usePermissions();
  const fileInputRef = useRef(null);
  const brandingFallbackRef = useRef({
    id: null,
    logo: branding.logo,
    companyName: branding.companyName,
    companySubtitle: branding.companySubtitle,
  });
  const updateBrandingRef = useRef(updateBranding);

  const canCreateSettings = can(SETTINGS_ACTION_PERMISSIONS.create);
  const canReadSettings = can(SETTINGS_ACTION_PERMISSIONS.read);
  const canUpdateSettings = can(SETTINGS_ACTION_PERMISSIONS.update);
  const canDeleteSettings = can(SETTINGS_ACTION_PERMISSIONS.delete);
  const canWriteSettings = canCreateSettings || canUpdateSettings;
  
  const [localName, setLocalName] = useState(branding.companyName);
  const [localSubtitle, setLocalSubtitle] = useState(branding.companySubtitle);
  const [localSidebarColor, setLocalSidebarColor] = useState(branding.sidebarColor);
  const [localAccentColor, setLocalAccentColor] = useState(branding.accentColor);
  const [localLogo, setLocalLogo] = useState(branding.logo);
  const [savedApiBranding, setSavedApiBranding] = useState({
    id: null,
    logo: branding.logo,
    companyName: branding.companyName,
    companySubtitle: branding.companySubtitle,
  });
  const [isDragging, setIsDragging] = useState(false);
  const [systemSettings, setSystemSettings] = useState(DEFAULT_SYSTEM_SETTINGS);
  const [savedSystemSettings, setSavedSystemSettings] = useState(DEFAULT_SYSTEM_SETTINGS);
  const [isSettingsLoading, setIsSettingsLoading] = useState(true);
  const [isSettingsSaving, setIsSettingsSaving] = useState(false);

  useEffect(() => {
    updateBrandingRef.current = updateBranding;
  }, [updateBranding]);

  useEffect(() => {
    let isMounted = true;

    const loadSettings = async () => {
      if (!canReadSettings) {
        setIsSettingsLoading(false);
        toast.error('Nao tem permissao para consultar as definicoes do sistema.');
        return;
      }

      setIsSettingsLoading(true);
      try {
        const [settingsResponse, companyResponse] = await Promise.all([
          api.get(settingsEndpoints.list),
          fetchCompanyInfo(),
        ]);
        const nextSettings = normalizeSystemSettings(settingsResponse.data);
        const nextBranding = normalizeCompanyInfo(companyResponse.data, brandingFallbackRef.current);
        if (!isMounted) return;
        setSystemSettings(nextSettings);
        setSavedSystemSettings(nextSettings);
        setLocalLogo(nextBranding.logo);
        setLocalName(nextBranding.companyName);
        setLocalSubtitle(nextBranding.companySubtitle);
        setSavedApiBranding(nextBranding);
        updateBrandingRef.current(nextBranding);
      } catch (error) {
        if (isMounted) {
          toast.error(getApiErrorMessage(error, 'Nao foi possivel carregar as definicoes do sistema.'));
        }
      } finally {
        if (isMounted) setIsSettingsLoading(false);
      }
    };

    loadSettings();

    return () => {
      isMounted = false;
    };
  }, [canReadSettings]);

  const handleLogoUpload = (file) => {
    if (!canWriteSettings) {
      toast.error('Nao tem permissao para alterar as definicoes do sistema.');
      return;
    }

    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Por favor, selecione um ficheiro de imagem (PNG, JPG, SVG).');
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      toast.error('A imagem deve ter no máximo 2MB.');
      return;
    }
    const reader = new FileReader();
    reader.onload = (e) => {
      setLocalLogo(e.target.result);
    };
    reader.readAsDataURL(file);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    if (!canWriteSettings) return;
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    if (!canWriteSettings) {
      toast.error('Nao tem permissao para alterar as definicoes do sistema.');
      return;
    }

    const file = e.dataTransfer.files[0];
    handleLogoUpload(file);
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    handleLogoUpload(file);
  };

  const removeLogo = () => {
    if (!canDeleteSettings) {
      toast.error('Nao tem permissao para remover definicoes do sistema.');
      return;
    }

    setLocalLogo(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleSave = async () => {
    if (!canWriteSettings) {
      toast.error('Nao tem permissao para guardar as definicoes do sistema.');
      return;
    }

    const nextApiBranding = {
      id: savedApiBranding.id,
      logo: localLogo,
      companyName: localName,
      companySubtitle: localSubtitle,
    };
    const hasApiBrandingChanges =
      nextApiBranding.logo !== savedApiBranding.logo ||
      nextApiBranding.companyName !== savedApiBranding.companyName ||
      nextApiBranding.companySubtitle !== savedApiBranding.companySubtitle;

    if (!hasApiBrandingChanges) {
      saveLocalBrandingColors();
      toast.success('Identidade visual guardada com sucesso!');
      return;
    }

    try {
      const response = await updateCompanyInfo(companyInfoPayload(nextApiBranding));
      const savedBranding = normalizeCompanyInfo(response.data, nextApiBranding, nextApiBranding.id);
      setLocalLogo(savedBranding.logo);
      setLocalName(savedBranding.companyName);
      setLocalSubtitle(savedBranding.companySubtitle);
      setSavedApiBranding(savedBranding);
      updateBranding({
        ...savedBranding,
        sidebarColor: localSidebarColor,
        accentColor: localAccentColor,
      });
      toast.success('Identidade visual guardada com sucesso!');
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Nao foi possivel guardar a identidade visual.'));
    }
  };

  const saveLocalBrandingColors = () => {
    updateBranding({
      logo: savedApiBranding.logo,
      companyName: savedApiBranding.companyName,
      companySubtitle: savedApiBranding.companySubtitle,
      sidebarColor: localSidebarColor,
      accentColor: localAccentColor,
    });
  };

  const updateSystemSetting = (key, value) => {
    if (!canWriteSettings) {
      toast.error('Nao tem permissao para alterar as definicoes do sistema.');
      return;
    }

    setSystemSettings(prev => ({ ...prev, [key]: value }));
  };

  const handleSystemSettingsSave = async () => {
    if (!canWriteSettings) {
      toast.error('Nao tem permissao para guardar as definicoes do sistema.');
      return;
    }

    setIsSettingsSaving(true);
    try {
      const response = await api.post(settingsEndpoints.update, systemSettingsPayload(systemSettings));
      const nextSettings = normalizeSystemSettings(response.data, systemSettings);
      setSystemSettings(nextSettings);
      setSavedSystemSettings(nextSettings);
      toast.success('Definicoes do sistema guardadas com sucesso!');
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Nao foi possivel guardar as definicoes do sistema.'));
    } finally {
      setIsSettingsSaving(false);
    }
  };

  const handleReset = async () => {
    if (!canDeleteSettings) {
      toast.error('Nao tem permissao para repor as definicoes do sistema.');
      return;
    }

    const defaultApiBranding = {
      id: savedApiBranding.id,
      logo: DEFAULT_BRANDING.logo,
      companyName: DEFAULT_BRANDING.companyName,
      companySubtitle: DEFAULT_BRANDING.companySubtitle,
    };

    try {
      const response = await updateCompanyInfo(companyInfoPayload(defaultApiBranding));
      const savedBranding = normalizeCompanyInfo(response.data, defaultApiBranding, defaultApiBranding.id);
      resetBranding();
      setLocalName(savedBranding.companyName);
      setLocalSubtitle(savedBranding.companySubtitle);
      setLocalSidebarColor(DEFAULT_BRANDING.sidebarColor);
      setLocalAccentColor(DEFAULT_BRANDING.accentColor);
      setLocalLogo(savedBranding.logo);
      setSavedApiBranding(savedBranding);
      updateBranding({
        ...savedBranding,
        sidebarColor: DEFAULT_BRANDING.sidebarColor,
        accentColor: DEFAULT_BRANDING.accentColor,
      });
      toast.success('Definições repostas para o padrão.');
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Nao foi possivel repor a identidade visual.'));
    }
  };

  const selectPreset = (preset) => {
    if (!canWriteSettings) {
      toast.error('Nao tem permissao para alterar as definicoes do sistema.');
      return;
    }

    setLocalSidebarColor(preset.sidebar);
    setLocalAccentColor(preset.accent);
  };

  const hasChanges = 
    localLogo !== savedApiBranding.logo ||
    localName !== savedApiBranding.companyName ||
    localSubtitle !== savedApiBranding.companySubtitle ||
    localSidebarColor !== branding.sidebarColor ||
    localAccentColor !== branding.accentColor;
  const hasSystemSettingsChanges = JSON.stringify(systemSettings) !== JSON.stringify(savedSystemSettings);
  const brandingControlsDisabled = !canWriteSettings;
  const settingsControlsDisabled = isSettingsLoading || isSettingsSaving || !canWriteSettings;

  // Determine if sidebar preview is light
  const isLight = (hex) => {
    const c = hex.replace('#', '');
    const r = parseInt(c.substring(0, 2), 16);
    const g = parseInt(c.substring(2, 4), 16);
    const b = parseInt(c.substring(4, 6), 16);
    return (0.299 * r + 0.587 * g + 0.114 * b) / 255 > 0.5;
  };

  const previewLight = isLight(localSidebarColor);
  const previewText = previewLight ? '#1e293b' : '#ffffff';
  const previewSubtext = previewLight ? '#64748b' : '#94a3b8';

  return (
    <Layout>
      <div className="space-y-6">
        <div>
          <h1 className="text-slate-900 text-3xl font-secondary font-bold tracking-tight">Sistema</h1>
          <p className="text-slate-600 font-primary mt-1">Configurações do sistema e preferências</p>
        </div>

        {/* ===== IDENTIDADE VISUAL ===== */}
        <Card className="p-6 border-2 border-blue-200 bg-gradient-to-br from-white to-blue-50/30">
          <div className="flex items-center gap-3 mb-6">
            <div className="bg-gradient-to-br from-blue-600 to-purple-600 p-3 rounded-lg">
              <Palette className="text-white" size={24} />
            </div>
            <div>
              <h2 className="text-slate-900 font-secondary font-bold text-lg">Identidade Visual</h2>
              <p className="text-slate-600 text-sm">Personalize a logomarca e as cores do painel</p>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left Column: Logo + Company Info */}
            <div className="space-y-5">
              {/* Logo Upload */}
              <div>
                <Label className="text-sm font-semibold mb-3 block">Logomarca</Label>
                <div
                  className={`relative border-2 border-dashed rounded-xl p-6 text-center transition-all cursor-pointer
                    ${isDragging ? 'border-blue-500 bg-blue-50' : 'border-slate-300 hover:border-blue-400 hover:bg-slate-50'}`}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                  onClick={() => {
                    if (brandingControlsDisabled) return;
                    fileInputRef.current?.click();
                  }}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handleFileSelect}
                    disabled={brandingControlsDisabled}
                  />
                  {localLogo ? (
                    <div className="space-y-3">
                      <div className="relative inline-block">
                        <img 
                          src={localLogo} 
                          alt="Logo preview" 
                          className="max-h-20 max-w-full mx-auto object-contain rounded-lg"
                        />
                        <button
                          onClick={(e) => { e.stopPropagation(); removeLogo(); }}
                          disabled={!canDeleteSettings}
                          className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition-colors shadow-md"
                        >
                          <X size={12} />
                        </button>
                      </div>
                      <p className="text-xs text-slate-500">Clique para alterar</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <div className="w-12 h-12 bg-slate-100 rounded-full flex items-center justify-center mx-auto">
                        <Upload className="text-slate-400" size={20} />
                      </div>
                      <p className="text-sm text-slate-600 font-medium">Arraste ou clique para carregar</p>
                      <p className="text-xs text-slate-400">PNG, JPG ou SVG (máx. 2MB)</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Company Name */}
              <div className="space-y-2">
                <Label className="text-sm font-semibold">Nome da Empresa</Label>
                <Input 
                  value={localName} 
                  onChange={(e) => setLocalName(e.target.value)}
                  placeholder="FleetControl"
                  disabled={brandingControlsDisabled}
                />
              </div>

              {/* Subtitle */}
              <div className="space-y-2">
                <Label className="text-sm font-semibold">Subtítulo</Label>
                <Input 
                  value={localSubtitle} 
                  onChange={(e) => setLocalSubtitle(e.target.value)}
                  disabled={brandingControlsDisabled}
                  placeholder="Gestão de Frotas"
                />
              </div>
            </div>

            {/* Middle Column: Color Selection */}
            <div className="space-y-5">
              {/* Color Presets */}
              <div>
                <Label className="text-sm font-semibold mb-3 block">Temas Predefinidos</Label>
                <div className="grid grid-cols-3 gap-2">
                  {COLOR_PRESETS.map((preset) => {
                    const selected = localSidebarColor === preset.sidebar && localAccentColor === preset.accent;
                    return (
                      <button
                        key={preset.name}
                        onClick={() => selectPreset(preset)}
                        disabled={brandingControlsDisabled}
                        className={`relative rounded-lg p-2 border-2 transition-all text-left group
                          ${selected ? 'border-blue-500 ring-2 ring-blue-200' : 'border-slate-200 hover:border-slate-300'}`}
                      >
                        <div className="flex gap-1 mb-1.5">
                          <div 
                            className="w-6 h-6 rounded-md border border-slate-200" 
                            style={{ backgroundColor: preset.sidebar }}
                          />
                          <div 
                            className="w-6 h-6 rounded-md" 
                            style={{ backgroundColor: preset.accent }}
                          />
                        </div>
                        <p className="text-[10px] text-slate-600 leading-tight truncate">{preset.name}</p>
                        {selected && (
                          <div className="absolute -top-1.5 -right-1.5 bg-blue-500 text-white rounded-full p-0.5">
                            <Check size={10} />
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Custom Colors */}
              <div className="space-y-3">
                <Label className="text-sm font-semibold">Cores Personalizadas</Label>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label className="text-xs text-slate-500">Barra Lateral</Label>
                    <div className="flex items-center gap-2">
                      <input 
                        type="color" 
                        value={localSidebarColor}
                        onChange={(e) => setLocalSidebarColor(e.target.value)}
                        disabled={brandingControlsDisabled}
                        className="w-10 h-10 rounded-lg cursor-pointer border border-slate-200"
                      />
                      <Input 
                        value={localSidebarColor}
                        onChange={(e) => setLocalSidebarColor(e.target.value)}
                        className="text-xs font-mono h-10"
                        maxLength={7}
                        disabled={brandingControlsDisabled}
                      />
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs text-slate-500">Cor de Destaque</Label>
                    <div className="flex items-center gap-2">
                      <input 
                        type="color" 
                        value={localAccentColor}
                        onChange={(e) => setLocalAccentColor(e.target.value)}
                        disabled={brandingControlsDisabled}
                        className="w-10 h-10 rounded-lg cursor-pointer border border-slate-200"
                      />
                      <Input 
                        value={localAccentColor}
                        onChange={(e) => setLocalAccentColor(e.target.value)}
                        className="text-xs font-mono h-10"
                        maxLength={7}
                        disabled={brandingControlsDisabled}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column: Preview */}
            <div>
              <Label className="text-sm font-semibold mb-3 block">Pré-visualização</Label>
              <div 
                className="rounded-xl overflow-hidden shadow-lg border border-slate-200"
                style={{ width: '100%' }}
              >
                {/* Preview Sidebar Header */}
                <div 
                  className="p-3"
                  style={{ 
                    backgroundColor: localSidebarColor,
                    borderBottom: `1px solid ${previewLight ? 'rgba(0,0,0,0.1)' : 'rgba(255,255,255,0.08)'}`
                  }}
                >
                  {localLogo ? (
                    <div className="flex flex-col items-center gap-1">
                      <img 
                        src={localLogo} 
                        alt="Preview" 
                        className="w-full max-h-10 object-contain rounded-md"
                        style={{ backgroundColor: previewLight ? 'transparent' : 'rgba(255,255,255,0.1)' }}
                      />
                      <p className="text-[9px]" style={{ color: previewSubtext }}>
                        {localSubtitle || 'Gestão de Frotas'}
                      </p>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2.5">
                      <div className="rounded-md p-1.5" style={{ backgroundColor: localAccentColor }}>
                        <ImageIcon className="text-white" size={16} />
                      </div>
                      <div>
                        <p className="font-bold text-sm leading-tight" style={{ color: previewText }}>
                          {localName || 'FleetControl'}
                        </p>
                        <p className="text-[10px]" style={{ color: previewSubtext }}>
                          {localSubtitle || 'Gestão de Frotas'}
                        </p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Preview Menu Items */}
                <div className="p-2 space-y-0.5" style={{ backgroundColor: localSidebarColor }}>
                  <div 
                    className="flex items-center gap-2 px-2.5 py-1.5 rounded-md text-white text-xs font-medium"
                    style={{ backgroundColor: localAccentColor }}
                  >
                    <div className="w-3.5 h-3.5 bg-white/20 rounded-sm" />
                    Dashboard
                  </div>
                  <div 
                    className="flex items-center gap-2 px-2.5 py-1.5 rounded-md text-xs"
                    style={{ color: previewLight ? '#475569' : '#cbd5e1' }}
                  >
                    <div className="w-3.5 h-3.5 rounded-sm" style={{ backgroundColor: previewLight ? 'rgba(0,0,0,0.1)' : 'rgba(255,255,255,0.15)' }} />
                    Tags Online
                  </div>
                  <div 
                    className="flex items-center gap-2 px-2.5 py-1.5 rounded-md text-xs"
                    style={{ color: previewLight ? '#475569' : '#cbd5e1' }}
                  >
                    <div className="w-3.5 h-3.5 rounded-sm" style={{ backgroundColor: previewLight ? 'rgba(0,0,0,0.1)' : 'rgba(255,255,255,0.15)' }} />
                    Ocorrências
                  </div>
                  <div 
                    className="flex items-center gap-2 px-2.5 py-1.5 rounded-md text-xs"
                    style={{ color: previewLight ? '#475569' : '#cbd5e1' }}
                  >
                    <div className="w-3.5 h-3.5 rounded-sm" style={{ backgroundColor: previewLight ? 'rgba(0,0,0,0.1)' : 'rgba(255,255,255,0.15)' }} />
                    Configurações
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-2 mt-4">
                <Button 
                  variant="outline" 
                  size="sm"
                  className="flex-1"
                  onClick={handleReset}
                  disabled={!canDeleteSettings}
                >
                  <RotateCcw size={14} className="mr-1.5" />
                  Repor Padrão
                </Button>
                <Button 
                  size="sm"
                  className="flex-1"
                  style={{ backgroundColor: localAccentColor }}
                  onClick={handleSave}
                  disabled={!hasChanges || brandingControlsDisabled}
                >
                  <Check size={14} className="mr-1.5" />
                  Guardar
                </Button>
              </div>
            </div>
          </div>
        </Card>

        {/* ===== EXISTING SETTINGS ===== */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card className="p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="bg-blue-50 p-3 rounded-lg">
                <Bell className="text-blue-600" size={24} />
              </div>
              <div>
                <h2 className="text-slate-900 font-secondary font-bold text-lg">Notificações</h2>
                <p className="text-slate-600 text-sm">Configurar alertas e notificações</p>
              </div>
            </div>

            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                <div>
                  <Label className="text-sm font-medium">Email de Ocorrências</Label>
                  <p className="text-xs text-slate-600 mt-1">Receber email para novas ocorrências</p>
                </div>
                <Switch
                  checked={systemSettings.occurrenceEmail}
                  onCheckedChange={(checked) => updateSystemSetting('occurrenceEmail', checked)}
                  disabled={settingsControlsDisabled}
                />
              </div>

              <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                <div>
                  <Label className="text-sm font-medium">Notificações Push</Label>
                  <p className="text-xs text-slate-600 mt-1">Alertas em tempo real no navegador</p>
                </div>
                <Switch
                  checked={systemSettings.pushNotifications}
                  onCheckedChange={(checked) => updateSystemSetting('pushNotifications', checked)}
                  disabled={settingsControlsDisabled}
                />
              </div>

              <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                <div>
                  <Label className="text-sm font-medium">Relatórios Semanais</Label>
                  <p className="text-xs text-slate-600 mt-1">Resumo semanal por email</p>
                </div>
                <Switch
                  checked={systemSettings.weeklyReports}
                  onCheckedChange={(checked) => updateSystemSetting('weeklyReports', checked)}
                  disabled={settingsControlsDisabled}
                />
              </div>

              <Button
                size="sm"
                className="w-full"
                onClick={handleSystemSettingsSave}
                disabled={!hasSystemSettingsChanges || settingsControlsDisabled}
              >
                {isSettingsSaving ? (
                  <Loader2 size={14} className="mr-1.5 animate-spin" />
                ) : (
                  <Check size={14} className="mr-1.5" />
                )}
                Guardar Notificacoes
              </Button>
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="bg-emerald-50 p-3 rounded-lg">
                <Database className="text-emerald-600" size={24} />
              </div>
              <div>
                <h2 className="text-slate-900 font-secondary font-bold text-lg">Dados</h2>
                <p className="text-slate-600 text-sm">Gestão e exportação de dados</p>
              </div>
            </div>

            <div className="space-y-4">
              <div className="p-4 bg-slate-50 rounded-lg">
                <Label className="text-sm font-medium mb-2 block">Retenção de Dados</Label>
                <p className="text-xs text-slate-600 mb-3">Manter histórico de ocorrências por</p>
                <select
                  value={systemSettings.dataRetention}
                  onChange={(e) => updateSystemSetting('dataRetention', e.target.value)}
                  disabled={settingsControlsDisabled}
                  className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <option value="30_days">30 dias</option>
                  <option value="90_days">90 dias</option>
                  <option value="6_months">6 meses</option>
                  <option value="1_year">1 ano</option>
                  <option value="persistent">Permanente</option>
                </select>
              </div>

              <Button
                size="sm"
                className="w-full"
                onClick={handleSystemSettingsSave}
                disabled={!hasSystemSettingsChanges || settingsControlsDisabled}
              >
                {isSettingsSaving ? (
                  <Loader2 size={14} className="mr-1.5 animate-spin" />
                ) : (
                  <Check size={14} className="mr-1.5" />
                )}
                Guardar Dados
              </Button>

              <div className="p-4 bg-slate-50 rounded-lg">
                <Label className="text-sm font-medium mb-3 block">Exportar Dados</Label>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" className="flex-1">Tags</Button>
                  <Button variant="outline" size="sm" className="flex-1">Ocorrências</Button>
                  <Button variant="outline" size="sm" className="flex-1">Relatório</Button>
                </div>
              </div>
            </div>
          </Card>
        </div>

      </div>
    </Layout>
  );
};
